import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import TabUsageInner from './TabUsageInner.jsx';
import { slugify } from '../../utilities/slugify';
import { useSwipe } from './swipe';

/**
 * Where the slider sits partway from one tab to the next: `progress` 0 is `from`, 1 is `to`. Each
 * is a tab's { left, width } within the tab bar.
 */
export function sliderBetween(from, to, progress) {
	const p = Math.min(Math.max(progress, 0), 1);
	return {
		left: from.left + (to.left - from.left) * p,
		width: from.width + (to.width - from.width) * p,
	};
}

/**
 * How tall a swipe area starting `top` px down the document has to be to reach the bottom of a
 * `viewportHeight` screen, so a swipe on the empty space under a short tab still lands.
 */
export function reachBottom(top, viewportHeight) {
	return Math.max(0, Math.round(viewportHeight - top));
}

// Past the first or last tab a drag still moves the content, this much of the finger's travel, so
// the edge is felt rather than the swipe just doing nothing.
const EDGE_RESISTANCE = 0.25;

/**
 * A row of tabs and the content of the selected one. `activeTab` is the selected tab's slug; when it
 * names none of them, the first is shown. A click asks for another through `onTabChange(slug)`.
 *
 * The selected tab is marked by a slider drawn under the tab bar, which moves to whichever tab is
 * selected however that happened. With `swipeable`, a touch swiping across the content steps to the
 * tab beside it, and the content and the slider follow the finger on the way; the area reaches the
 * bottom of the screen however short the tab is. `swipeDisabled` holds
 * that off: the calendar passes its react-beautiful-dnd drag, which a touch starts with a hold.
 */
export default function Tabs({
	children,
	activeTab = null,
	onTabChange,
	onTabClick: onTabClickProp,
	tabsClasses = '',
	tabContentClasses = '',
	showHeadings = false,
	swipeable = false,
	swipeDisabled = false,
}) {
	const tabs = React.Children.toArray(children).filter(Boolean);
	const slugs = tabs.map((tab) => slugify(tab.props.label));
	const activeIndex = Math.max(slugs.indexOf(activeTab), 0);
	const activeSlug = slugs[activeIndex];

	const tabsRef = useRef(null);
	const sliderRef = useRef(null);
	const contentRef = useRef(null);
	const areaRef = useRef(null);
	const [tabsInstance, setTabsInstance] = useState(null);
	// The tab a swipe brought, and from which side, so it can slide in.
	const [arrival, setArrival] = useState({ slug: null, direction: 0 });

	useEffect(() => {
		setTabsInstance(M.Tabs.init(tabsRef.current, {}));
	}, []);

	// Measured against the bar rather than by offsetLeft: each tab is positioned, to sit over the
	// slider, which would make it the link's offset parent.
	const tabRect = useCallback((index) => {
		const bar = tabsRef.current;
		const link = bar && bar.querySelectorAll('li.tab > a')[index];
		if(!link) {
			return null;
		}
		const barBox = bar.getBoundingClientRect();
		const linkBox = link.getBoundingClientRect();
		return {
			left: linkBox.left - barBox.left - bar.clientLeft + bar.scrollLeft,
			width: linkBox.width,
		};
	}, []);

	const placeSlider = useCallback((rect) => {
		const slider = sliderRef.current;
		if(slider && rect) {
			slider.style.transform = `translateX(${rect.left}px)`;
			slider.style.width = `${rect.width}px`;
		}
	}, []);

	useLayoutEffect(() => {
		placeSlider(tabRect(activeIndex));
	});

	useEffect(() => {
		const settle = () => placeSlider(tabRect(activeIndex));
		window.addEventListener('resize', settle);
		return () => window.removeEventListener('resize', settle);
	}, [activeIndex]);

	// A min-height rather than stretching the boxes above it: those carry `height: 100%` rules that
	// only come to nothing while nothing above them has a height, and the calendar's grid inherits
	// them. A min-height on the area alone changes no other box.
	useLayoutEffect(() => {
		const area = areaRef.current;
		if(!swipeable || !area) {
			return undefined;
		}
		const fit = () => {
			const top = area.getBoundingClientRect().top + window.scrollY;
			area.style.minHeight = `${reachBottom(top, window.innerHeight)}px`;
		};
		fit();
		window.addEventListener('resize', fit);
		return () => window.removeEventListener('resize', fit);
	}, [swipeable, activeIndex]);

	const selectTab = useCallback((slug) => {
		if(onTabClickProp) {
			onTabClickProp(slug);
		}
		if(onTabChange && slug !== activeSlug) {
			onTabChange(slug);
		}
	}, [onTabClickProp, onTabChange, activeSlug]);

	const followFinger = useCallback((dx) => {
		const content = contentRef.current;
		const slider = sliderRef.current;
		const dragging = dx !== 0;
		if(slider) {
			slider.classList.toggle('is-following', dragging);
		}
		if(content) {
			content.classList.toggle('is-following', dragging);
		}
		const neighbour = activeIndex + (dx < 0 ? 1 : -1);
		const hasNeighbour = dragging && neighbour >= 0 && neighbour < tabs.length;
		const width = content ? content.clientWidth : 0;
		if(content) {
			const shift = hasNeighbour ? dx : dx * EDGE_RESISTANCE;
			content.style.transform = dragging ? `translateX(${shift}px)` : '';
			content.style.opacity = dragging && hasNeighbour && width ? String(1 - Math.min(Math.abs(dx) / width, 1) * 0.5) : '';
		}
		const from = tabRect(activeIndex);
		if(!hasNeighbour || !width) {
			placeSlider(from);
			return;
		}
		placeSlider(sliderBetween(from, tabRect(neighbour), Math.abs(dx) / width));
	}, [activeIndex, tabs.length]);

	const swipe = useSwipe({
		onSwipe: swipeable ? (direction) => {
			const next = activeIndex + direction;
			if(next >= 0 && next < tabs.length) {
				setArrival({ slug: slugs[next], direction });
				selectTab(slugs[next]);
			}
		} : null,
		onDrag: followFinger,
		disabled: swipeDisabled,
	});

	const activeChild = tabs[activeIndex];
	const arriving = arrival.slug === activeSlug
		? (arrival.direction > 0 ? ' is-arriving-next' : ' is-arriving-previous')
		: '';

	return (
		<React.Fragment>
			<div className={`row tab-header ${tabsClasses ? tabsClasses : ''} `}>
				<ul
					ref={tabsRef}
					className='tabs'>
					<li className='tabs__slider' ref={sliderRef} aria-hidden='true' />
					{tabs.map((tab, index) => {
						const { content, label, icon } = tab.props;
						return (
							<TabUsageInner
								tabsInstance={tabsInstance}
								onTabClick={selectTab}
								activeTab={activeSlug}
								index={index}
								key={label}
								content={content}
								label={label}
								icon={icon}
							/>
						);
					})}
				</ul>
			</div>
			<div
				ref={areaRef}
				className={`${tabContentClasses} tab-content${swipeable ? ' is-swipeable' : ''}`}
				{...(swipeable ? swipe : {})}
			>
				{activeChild &&
				<div
					ref={contentRef}
					className={`${activeChild.props.className || ''} tab-content-inner active${arriving}`}
					key={activeChild.props.label}
					id={activeSlug}>
					{showHeadings &&
					<div className='container'>
						<div className='row'>
							<div className='col s12'>
								<h5>{activeChild.props.label}</h5>
							</div>
						</div>
					</div>
					}
					{activeChild.props.children}
				</div>
				}
			</div>
		</React.Fragment>
	);
}

Tabs.propTypes = {
	children: PropTypes.node.isRequired,
	onTabChange: PropTypes.func,
	onTabClick: PropTypes.func,
	tabsClasses: PropTypes.string,
	tabContentClasses: PropTypes.string,
	activeTab: PropTypes.string,
	showHeadings: PropTypes.bool,
	swipeable: PropTypes.bool,
	swipeDisabled: PropTypes.bool,
};
